import { ArgumentError } from '../utils/errors';
import BookingModel from './bookingModel';
import { BaseAPIModel, fetcher } from '@weezevent/weezjs-core';
import { capitalizeFirstLetter } from '../pages/attendee/detail/components/utils';
import i18n from 'i18next';

export default class AttendeeModel extends BaseAPIModel {
    fields() {
        return {
            id: { primaryKey: true, readOnly: true },
            ticket_url: {}
        };
    }

    static fromJSON(attendee) {
        attendee = super.fromJSON(attendee);
        attendee.booking = attendee.booking ? BookingModel.fromJSON(attendee.booking) : null;
        return attendee;
    }

    // TODO: handle limit/offset rules
    static listUrl({ organizationId, eventId }) {
        if (!organizationId || !eventId) {
            throw new ArgumentError(`[Model][AttendeeModel][getAll] organizationId and eventId are mandatory`);
        }

        return `/ticket/organizations/${organizationId}/events/${eventId}/attendees`;
    }

    createBooking(data) {
        this.booking = new BookingModel(data);
    }

    isMatchingSeat(seat) {
        //case no booking
        if (!this.object.booking) {
            return false;
        }

        //case general admission
        if (!this.object.booking.seat_uuid && this.object.booking.area_zone) {
            return false;
        }

        //case category or contingent not matching
        if (this.object.booking.seat_category !== seat.category || this.object.booking.contingent !== seat.contingent) {
            return false;
        }

        return true;
    }

    get name() {
        return `${this.object.first_name} ${this.object.last_name}`;
    }

    get resolved_name() {
        if (this.object.last_name) {
            return `${this.object.last_name.toUpperCase()} ${capitalizeFirstLetter(this.object.first_name)}`;
        }
        if (!this.object.last_name && this.object.order.buyer_last_name) {
            return `${this.object.order.buyer_last_name.toUpperCase()} ${capitalizeFirstLetter(this.object.order.buyer_first_name)}`;
        }
        return i18n.t('ticket.manager.nonNominative').toUpperCase();
    }

    get resolved_attendee_name() {
        if (this.object.last_name) {
            return `${this.object.last_name.toUpperCase()} ${capitalizeFirstLetter(this.object.first_name)}`;
        }
        return i18n.t('ticket.manager.nonNominative').toUpperCase();
    }

    get resolved_buyer_name() {
        if (this.object.order.buyer_last_name) {
            return `${this.object.order.buyer_last_name.toUpperCase()} ${capitalizeFirstLetter(this.object.order.buyer_first_name)}`;
        }
        return i18n.t('ticket.manager.nonNominative').toUpperCase();
    }

    get resolved_mail() {
        return this.object.email && this.object.email !== '' ? this.object.email : this.object.order.buyer_email;
    }

    static perform_action(action, objects, params, isAsync = false) {
        if (!isAsync && !this[`perform_action_${action}`]) {
            throw new Error(`No action named ${action}`);
        }
        if (isAsync) {
            if (!this[`async_perform_action_${action}`]) {
                throw new Error(`No action named ${action}`);
            }
            return this[`async_perform_action_${action}`](objects, params);
        }
        return this[`perform_action_${action}`](objects, params);
    }

    static perform_action_print_ticket(data, { organization }) {
        return fetcher.post(`/ticket/organizations/${organization.id}/print`, { data: data });
    }

    static perform_action_restore(attendees, { organization, event }) {
        const data = attendees.map(attendee => ({ id: attendee.id }));
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/restore`, { data: data });
    }

    static async_perform_action_restore(attendees, { organization, event }) {
        let attendees_ids = attendees.map(element => element.id).join(',');
        const data = {
            type: 'async_restore',
            resource_query_filter: {
                id__in: attendees_ids
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/async_restore`, { data: data });
    }

    static perform_action_rotate_barcode(attendees, { organization, event, send_mail, to_reservation }) {
        const data = { ids: attendees.map(attendee => attendee.id), send_mail, to_reservation };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/rotate_barcode`, { data: data });
    }

    static async_perform_action_rotate_barcode(attendees, { organization, event, send_mail, to_reservation }) {
        let attendees_ids = attendees.map(element => element.id);
        const data = {
            type: 'async_bulk_rotate_barcode',
            resource_query_filter: {
                id__in: attendees_ids.join(',')
            },
            config: {
                send_mail,
                to_reservation
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/actions`, { data: data });
    }

    static perform_action_delete_refund_requests(data, { organization, event }) {
        return fetcher.delete(`/ticket/organizations/${organization.id}/events/${event.id}/refund_request`, { data: data });
    }

    static perform_action_refund(attendees, { organization, event, delete_all }) {
        const data = {
            attendee_ids: attendees.map(({ pk }) => pk),
            options: {
                delete_refunded: delete_all,
                dry_run: false,
                refund_charges: true
            }
        };

        return this.fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/refund`, { data: data });
    }

    static async_perform_action_refund(attendees, { organization, event, delete_all }) {
        let attendees_ids = attendees.map(element => element.id);
        const data = {
            type: 'async_bulk_refund',
            resource_query_filter: {
                id__in: attendees_ids.join(',')
            },
            config: {
                delete_refunded: delete_all,
                dry_run: false,
                refund_charges: true
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/actions`, { data: data });
    }

    static perform_action_send_ticket(attendees, { organization, event }) {
        const data = attendees.map(attendee => ({ id: attendee.id }));

        return fetcher
            .post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/send_mail`, { data: data })
            .then(({ response }) => ({ count: response['count'], initial_count: attendees.length }));
    }

    static async_perform_action_send_ticket(attendees, { organization, event }) {
        let attendees_ids = attendees.map(element => element.id);
        const data = {
            type: 'async_send_mail',
            resource_query_filter: {
                id__in: attendees_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/async_send_mail`, { data: data });
    }

    static perform_action_download_ticket(objects, { organization, event }) {
        let promise = Promise.resolve(objects);
        if (objects.length === 1) {
            // If there's only one attendee. just download with his url
            window.open(objects[0].ticket_url, '_blank');
        } else {
            // Else generate pdf with all tickets
            const data = objects.map(attendee => ({ id: attendee.id }));
            promise = fetcher
                .post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/download`, { data: data })
                .then(({ response }) => window.open(response, '_blank'));
        }
        return promise;
    }

    static perform_action_swap_session(data, { organization, event }) {
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/swap_session`, { data: data });
    }

    static perform_action_change_rate(data, { organization, event }) {
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/swap_rate`, { data: data });
    }

    static perform_action_update_moderation_status(data, organization, event, attendee) {
        return fetcher.patch(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/${attendee.id}/update_moderation_status`, { data: data });
    }

    static perform_action_bulk_update_moderation_status(objects, { event, organization, moderationStatus, notifyAttendees }) {
        const data = {
            moderation_status: moderationStatus,
            notify_attendee: notifyAttendees,
            ids: objects.map(attendee => attendee.id)
        };
        return fetcher.patch(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/bulk_update_moderation_status`, { data: data });
    }

    static async_perform_action_bulk_update_moderation_status(objects, { event, organization, moderationStatus, notifyAttendees }) {
        let attendees_ids = objects.map(element => element.id);
        const data = {
            type: 'async_bulk_update_moderation_status',
            resource_query_filter: {
                id__in: attendees_ids.join(',')
            },
            config: {
                moderation_status: moderationStatus,
                notify_attendee: notifyAttendees
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/actions`, { data: data });
    }

    static perform_action_delete(objects, { organization, event }) {
        let promise = this.destroy(objects, {
            organizationId: organization.pk,
            eventId: event.pk
        });
        return Promise.resolve(promise);
    }

    static async_perform_action_delete(objects, { organization, event }) {
        let attendees_ids = objects.map(element => element.id);
        const data = {
            type: 'async_bulk_destroy',
            resource_query_filter: {
                id__in: attendees_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/actions`, { data: data });
    }

    static perform_action_send_moderation_status(objects, { event, organization }) {
        const ids = {
            ids: objects.map(obj => obj.pk)
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/send_moderation_status`, { data: ids });
    }

    static async_perform_action_send_moderation_status(objects, { organization, event }) {
        let attendees_ids = objects.map(element => element.id);
        const data = {
            type: 'async_send_moderation_status',
            resource_query_filter: {
                id__in: attendees_ids.join(',')
            }
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/attendees/actions`, { data: data });
    }
}

AttendeeModel.fromSeatlib = seatlibAttendee => {
    return new AttendeeModel({
        id: seatlibAttendee.id(),
        barcode: seatlibAttendee.barcode(),
        booking_id: seatlibAttendee.bookingId(),
        first_name: seatlibAttendee.firstName(),
        last_name: seatlibAttendee.lastName(),
        email: seatlibAttendee.email(),
        order: seatlibAttendee.order(),
        id_weez_ticket: seatlibAttendee._idWeezTicket,
        scanned: seatlibAttendee.scanned(),
        paid_price: seatlibAttendee.paidPrice()
    });
};
